<script lang="ts" setup>
import { useI18n } from '@/composables/useI18n';

import { computed, nextTick, onUpdated, ref, watch } from 'vue';
// import { useWindowSize } from '@vueuse/core';
import { IPersistedTokenExtended, useSwapStore, useTokensStore } from '@/store';
import { TokenIcon } from '@/components/UI/TokenIcon';
import { getAmountWithDecimal } from '@/composables/useInputServices';
import { storeToRefs } from 'pinia';
import { TokenAlert } from '@/components/UI/TokenAlert';
import { GetTradeRoute } from '@/api/types';
import { SelectButtonChangeEvent } from 'primevue/selectbutton';

enum VIEW_STATE {
  ROUTE_COLLAPSED = 'ROUTE_COLLAPSED',
  ROUTE_EXPANDED = 'ROUTE_EXPANDED',
  ROUTE_STRAIGHT = 'ROUTE_STRAIGHT',
  ROUTE_NOT_FOUND = 'ROUTE_NOT_FOUND',
  SELECT_SECOND_TOKEN = 'SELECT_SECOND_TOKEN',
  SELECT_FIRST_TOKEN = 'SELECT_FIRST_TOKEN',
}

const { t } = useI18n();
// const { width: windowWidth } = useWindowSize();
const swapStore = useSwapStore();
const tokensStore = useTokensStore();
const isShowHelp = ref(true);
const isShowRoute = ref(false);
const routeContainer = ref<HTMLElement | null>(null);
const {
  fromCurrency,
  toCurrency,
  swappingRoutes,
  smartRouterMode,
  currentModeError,
} = storeToRefs(swapStore);
const { setSmartRouterMode } = swapStore;
const sideIconsWidth = (24 + 6) * 2;
const isExpanded = ref(false);

interface IPossiblyScummed {
  symbol: string;
  type: string;
  alert: boolean;
}

onUpdated(() => {
  // console.log('onUpdated: ', routeContainer.value);
  const container = routeContainer.value as HTMLElement;
  const correctView = currentView.value === VIEW_STATE.ROUTE_EXPANDED;
  const isDrawn = container?.children.length > 3;
  // console.log('onUpdated isExpanded: ', correctView && isDrawn);
  isExpanded.value = correctView && isDrawn;
  if (isExpanded.value) {
    isNeedWrap();
  }
});

const tradeRouteTooltip = computed(() => ({
  value: t('help.swappingRoute'),
  class: 'swapping-route-tooltip',
}));

const tokens = computed<{
  from: IPersistedTokenExtended;
  to: IPersistedTokenExtended;
}>(() => {
  return {
    from: tokensStore.getToken(
      fromCurrency.value.token,
    ) as IPersistedTokenExtended,
    to: tokensStore.getToken(toCurrency.value.token) as IPersistedTokenExtended,
  };
});

const from = computed(() => {
  if (!fromCurrency.value.token || !tokens.value.from) return;
  const { decimals } = tokens.value.from;
  return {
    name: fromCurrency.value.token,
    token: tokens.value?.from,
    amount: getAmountWithDecimal(fromCurrency.value.amount, decimals),
  };
});

const to = computed(() => {
  if (!toCurrency.value.token || !tokens.value.to) return;
  const { decimals } = tokens.value.to;
  return {
    name: toCurrency.value.token,
    token: tokens.value?.to,
    amount: getAmountWithDecimal(toCurrency.value.amount, decimals),
  };
});

const route = computed<
  { name: string; token: IPersistedTokenExtended | IPossiblyScummed }[] | []
>(() => {
  return (
    swappingRoutes.value?.map((item) => ({
      name: item.outputToken,
      token: (tokensStore.getToken(
        item.outputToken,
      ) as IPersistedTokenExtended) || {
        symbol: item.outputToken.split('::').at(-1),
        type: item.outputToken,
        alert: true,
      },
    })) || []
  );
});

const formattedFromAmount = computed(() => {
  if (!from.value) return;
  return formatNumber(from.value?.amount || 0, from.value?.token?.decimals);
});

const formattedToAmount = computed(() => {
  if (!to.value) return;
  return formatNumber(to.value?.amount || 0, to.value?.token?.decimals);
});

const pathLength = computed(() => route.value.length);

const currentView = computed<VIEW_STATE>(() => {
  if (!from.value) return VIEW_STATE.SELECT_FIRST_TOKEN;
  if (!to.value) return VIEW_STATE.SELECT_SECOND_TOKEN;
  if (pathLength.value === 1) return VIEW_STATE.ROUTE_STRAIGHT;
  if (pathLength.value && pathLength.value > 1) {
    if (isShowRoute.value) {
      return VIEW_STATE.ROUTE_EXPANDED;
    } else {
      return VIEW_STATE.ROUTE_COLLAPSED;
    }
  }
  return VIEW_STATE.ROUTE_NOT_FOUND;
});

const smartRouteModeOptions = computed<
  { id: number; label: string; value: GetTradeRoute.SmartRouterMode }[]
>(() => [
  { id: 1, label: t('swap.direct'), value: 'direct' },
  { id: 2, label: t('swap.aggregated'), value: 'default' },
]);

watch(pathLength, () => {
  if (pathLength.value === 2) {
    isShowRoute.value = true;
  }
});

function isNeedWrap() {
  // console.log(' isNeedWrap isExpanded.value', isExpanded.value);
  if (!isExpanded.value) return false;
  // const container = routeContainer.value as HTMLElement;
  // console.log(
  //   ' isNeedWrap container.children.length',
  //   container.children.length,
  // );
  // console.log(
  //   ' isNeedWrap getParentWidth() - getChildrenWidth()',
  //   getParentWidth() - getChildrenWidth(),
  //   getParentWidth() - getChildrenWidth() <= 0,
  // );
  return getParentWidth() - getChildrenWidth() <= 0;
}

function isWrapping(previous: Element, current: Element) {
  const threshold = 20;
  // console.log('isWrapping previous: ', previous.innerHTML);
  // console.log('isWrapping current: ', current.innerHTML);
  // console.log(
  //   'isWrapping',
  //   current.getBoundingClientRect().top -
  //     previous.getBoundingClientRect().top >=
  //     threshold,
  // );
  return (
    current.getBoundingClientRect().top -
      previous.getBoundingClientRect().top >=
    threshold
  );
}

function highlightWrappingElements() {
  nextTick(() => {
    const container = routeContainer.value as HTMLElement;
    for (let i = 1; i < container?.children.length; ++i) {
      const previous = container?.children[i - 1];
      const current = container?.children[i];

      if (isWrapping(previous, current)) {
        previous.classList.add('wrapping');
        current.classList.add('next-line');
      }
    }
  });
}

function getChildrenWidth(): number {
  const childrenMinGap = 5;

  const container = routeContainer.value as HTMLElement;
  if (!container || container?.children.length <= 1) return 0;
  let width = 0;
  for (let i = 0; i < container?.children.length; ++i) {
    const current = container?.children[i];
    // console.log(
    //   `children ${current.textContent} width: `,
    //   current.clientWidth + childrenMinGap,
    // );
    width += current.clientWidth + childrenMinGap;
  }
  // console.log('getChildrenWidth', width);
  return Math.floor(width - sideIconsWidth);
}

function getParentWidth(): number {
  const container = routeContainer.value as HTMLElement;
  // console.log('getParentWidth', container?.clientWidth - sideIconsWidth);
  return container?.clientWidth - sideIconsWidth;
}

function handleSmartRouteModeChange(event: SelectButtonChangeEvent) {
  const mode = event.value as GetTradeRoute.SmartRouterMode;
  setSmartRouterMode(mode, true);
}

// function resetHighlight() {
//   nextTick(() => {
//     const container = routeContainer.value as HTMLElement;
//     for (let i = 1; i < container?.children.length; ++i) {
//       const current = container?.children[i];
//
//       current.classList.remove('wrapping');
//       current.classList.remove('next-line');
//     }
//   });
// }

function formatNumber(amount: number, decimals?: number) {
  const formatter = Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals || 2,
  });

  return formatter.format(amount);
}

watch(
  [isExpanded, () => from.value?.name, () => to.value?.name],
  (newValue, oldValue) => {
    const [newIsExpanded, newFrom, newTo] = newValue;
    const [oldIsExpanded, oldFrom, oldTo] = oldValue || [];
    if (
      newIsExpanded !== oldIsExpanded ||
      newFrom !== oldFrom ||
      newTo !== oldTo
    ) {
      // console.log('watch isNeedWrap.value', isNeedWrap());
      if (isNeedWrap()) {
        highlightWrappingElements();
      }
      if (newFrom !== oldFrom || newTo !== oldTo) {
        isShowRoute.value = false;
      }
    }
  },
);

watch(smartRouterMode, (newValue) => {
  if (newValue === 'direct') {
    isShowRoute.value = false;
  }
});

// throttledWatch(
//   windowWidth,
//   (newValue, oldValue) => {
//     // console.log('width', newValue, oldValue);
//     // console.log(
//     //   'isNeedWrap',
//     //   route.value.length &&
//     //     windowWidth.value < 768 &&
//     //     route.value.length % 2 === 0,
//     // );
//     // console.log('parentWidth:', getParentWidth());
//     // console.log('childrenWidth:', getChildrenWidth());
//   },
//   { throttle: 1000 },
// );
</script>

<template>
  <div class="swapping-route">
    <div class="swapping-route__header header">
      <img
        :alt="$t('swap.swappingRoute')"
        class="header__icon"
        height="30"
        src="@/assets/swapping-route.svg"
        width="30"
      />

      <div class="header__title">
        {{ $t('swap.swappingRoute') }}
      </div>
      <span
        v-if="isShowHelp"
        v-tooltip.top="tradeRouteTooltip"
        class="header__tooltip"
      >
        <img
          alt=""
          class="header__help"
          height="24"
          src="@/assets/help-circle.svg"
          width="24"
        />
      </span>
      <SelectButton
        :model-value="smartRouterMode"
        :options="smartRouteModeOptions"
        :unselectable="false"
        class="header__selector"
        option-disabled="disabled"
        option-label="label"
        option-value="value"
        @change="handleSmartRouteModeChange"
      />
    </div>
    <div class="swapping-route__route route">
      <div
        ref="routeContainer"
        :class="{ path_collapsed: !isShowRoute, path_expanded: isShowRoute }"
        class="route__path path"
      >
        <div class="path__start path__item">
          <template v-if="from">
            <TokenIcon
              :logo="from.token?.logo"
              :type="from.token?.type"
              class="icon"
              size="24"
            />
          </template>
          <template v-else>
            <span class="icon" />
          </template>
        </div>
        <template v-if="currentView === VIEW_STATE.ROUTE_COLLAPSED">
          <div class="path__button">
            <button @click="isShowRoute = true">
              {{ pathLength - 1 }}
              {{ $t('common.pluralizedToken', pathLength - 1) }}
              <span :class="['caret', isShowRoute ? 'caret_active' : '']" />
            </button>
            <div class="path__item"></div>
          </div>
        </template>
        <template v-else-if="currentView === VIEW_STATE.ROUTE_EXPANDED">
          <TransitionGroup appear name="list">
            <div
              v-for="(item, index) in route.slice(0, -1)"
              :key="`path-${item?.token?.symbol}-${index}`"
              :class="{
                item__first: index === 0,
                item__last: index === route.length - 2,
                [`of_${route.length - 1}`]: route.length > 1,
              }"
              class="path__item item"
            >
              <div class="item__wrapper">
                <div class="token">
                  <TokenIcon
                    :logo="(item?.token as IPersistedTokenExtended)?.logo"
                    :type="item?.token?.type"
                    class="path__icon"
                    size="24"
                  />
                  {{ item.token?.symbol }}
                  <template v-if="(item.token as IPossiblyScummed)?.alert">
                    <token-alert :forced="true" :type="item.token?.type" />
                  </template>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </template>
        <template v-else-if="currentView === VIEW_STATE.ROUTE_STRAIGHT" />
        <template v-else-if="currentView === VIEW_STATE.ROUTE_NOT_FOUND">
          <div class="path__message">{{ currentModeError.text }}</div>
        </template>
        <template v-else-if="currentView === VIEW_STATE.SELECT_SECOND_TOKEN">
          <div class="path__message">{{ $t('swap.selectSecondToken') }}</div>
        </template>
        <template v-else-if="currentView === VIEW_STATE.SELECT_FIRST_TOKEN">
          <div class="path__message">{{ $t('swap.selectFirstToken') }}</div>
        </template>
        <div class="path__end path__item">
          <template v-if="to">
            <TokenIcon
              :logo="to.token?.logo"
              :type="to.token?.type"
              class="icon"
              size="24"
            />
          </template>
          <template v-else>
            <span class="icon" />
          </template>
        </div>
      </div>
    </div>
    <div class="swapping-route__footer footer">
      <div class="footer__from">
        <Transition appear mode="out-in" name="footer">
          <span :key="`footer-${from?.amount}`">
            {{ formattedFromAmount }}
          </span>
        </Transition>
        <Transition appear mode="out-in" name="footer">
          <span :key="`footer-${from?.token?.symbol}`">
            {{ from?.token?.symbol || '' }}
          </span>
        </Transition>
        <i
          v-if="currentView !== VIEW_STATE.SELECT_FIRST_TOKEN"
          class="pi pi-arrow-right"
        />
      </div>

      <div class="footer__column">
        <button v-if="isShowRoute" @click="isShowRoute = false">
          {{ $t('swap.hideDetails') }}
        </button>
      </div>
      <div class="footer__to">
        <Transition appear mode="out-in" name="footer">
          <span :key="`footer-${to?.amount}`">
            {{ formattedToAmount }}
          </span>
        </Transition>
        <Transition appear mode="out-in" name="footer">
          <span :key="`footer-${to?.token?.symbol}`">
            {{ to?.token?.symbol || '' }}
          </span>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.swapping-route {
  border-radius: 24px;
  min-height: 11rem;
  color: white;
  background: var(--surface-card);
  box-shadow: rgb(0 0 0 / 1%) 0 0 1px, rgb(0 0 0 / 4%) 0 4px 8px,
    rgb(0 0 0 / 4%) 0 16px 24px, rgb(0 0 0 / 1%) 0 24px 32px;
  margin-bottom: 20px;
  container-type: inline-size;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0.75rem;
  }

  &__route {
    padding: 1rem;
    display: flex;
  }

  &__footer {
    min-height: 3.5rem;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
  }
}

.header {
  &__title {
    font-size: 1rem;
    color: var(--text-color);
    font-weight: 600;
    line-height: 1.5rem;
    letter-spacing: 0.01rem;
    @media screen and (max-width: 450px) {
      font-size: 4cqw;
    }
  }
  &__tooltip {
    display: flex;
    margin: auto 0;
  }

  &__selector {
    margin-left: auto;
    white-space: nowrap;
  }

  &:deep(.p-selectbutton) {
    height: 32px;

    justify-content: center;
    border-radius: 100px;

    & > .p-button {
      padding: 5px 15px;
      background-color: #24243a;
      &:hover {
        color: var(--text-color);
        border-color: #303046;
        background: #303046;
      }
      &:focus {
        box-shadow: 0 0 0 0.063rem rgba(196, 181, 253, 0.5);
      }

      & .p-button-label {
        font-weight: 600;
        font-size: 1rem;
        line-height: 22px;
        letter-spacing: -0.43%;
        @media screen and (max-width: 450px) {
          font-size: 4cqw;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }

      &.p-highlight {
        color: var(--text-color);
        background-color: #4a495e;
        border-color: #4a495e;
      }
    }

    &:not(.p-disabled):not(.p-highlight):hover {
      background-color: #303046;
    }
  }
}

.route {
  align-items: center;
  justify-content: center;
  min-height: 6rem;
}

.icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  outline: 6px solid #2b2b42;
}

.item {
  display: flex;
  &__wrapper {
    padding: 5px;
    margin: 0 auto;
  }
}

.path {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  &__item:not(:last-child):not(:first-child) {
    flex-grow: 1;
    justify-content: flex-end;
    position: relative;
    &:after,
    &:before {
      content: '';
      width: 50%;
      height: 2px;
      background-color: #2b2b42;
      position: absolute;
      bottom: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: -1;
    }
    &:after {
      right: 0;
    }
    &:before {
      left: 0;
    }

    &.wrapping:after {
      content: none;
    }

    &.next-line {
      &:before {
        content: none;
      }
    }
  }
  .token {
    border: 2px solid #3e3e52;
    border-radius: 8px;
    padding: 8px;
    background: var(--surface-card);
    display: flex;
    align-items: center;
    gap: 4px;
    letter-spacing: 0.028rem;
    color: #fbfaff;
    font-weight: 500;
    font-size: 1rem;
  }

  &__message,
  &__item:not(:last-child):not(:first-child) {
    z-index: 1;
    cursor: default;
    color: var(--text-color-secondary);
    background: var(--surface-card);
    font-size: 1rem;
    min-width: 4rem;
  }

  &__message {
    padding: 0 2rem;
    font-weight: 500;
  }

  &__start,
  &__end {
    display: flex;
    width: 1.9rem;
    & > .icon {
      z-index: 1;
    }
  }
  &__end {
    &:before {
      content: '';
      width: calc(100% - 60px);
      margin-left: 20px;
      margin-right: 20px;
      height: 2px;
      background-color: #2b2b42;
      position: absolute;
      left: 8px;
      bottom: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 0;
    }
  }

  &__button {
    margin: 0 auto;
    z-index: 2;
    position: relative;
    width: 7.375rem;
    height: 2.5rem;
    border-radius: 8px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      background: var(--surface-card);

      color: var(--text-color-secondary);
      cursor: pointer;
      z-index: 1;
      border: 2px solid #3e3e52;
      gap: 8px;
      width: 100%;
      height: 100%;
      border-radius: 8px;
      text-transform: capitalize;
      line-height: 1.375rem;
      letter-spacing: 0.01rem;
    }

    .path__item {
      position: absolute;
      z-index: 0;
      border: 2px solid #3e3e52;
      border-radius: 8px;
      padding: 8px;
      width: 5.75rem;
      background: var(--surface-card);
      top: 65%;
      transform: translateX(-50%);
      left: 50%;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
  }
}

.footer {
  position: relative;
  &__from,
  &__to {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.5rem;
    letter-spacing: 0.01rem;
    color: var(--text-color-secondary);
  }
  &__column {
    position: absolute;
    margin-top: -1.5rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    button {
      background: none;
      border: 0;
      color: var(--text-color-secondary);
      cursor: pointer;
    }
  }
}
.wrapping {
  margin-right: 2rem;
}
.next-line {
  margin-left: auto;
}

.caret {
  background-color: #d4c4ed;
  mask: url('@/assets/pools/dropdown.svg') no-repeat center;
  width: 10px;
  height: 10px;
}

.swapping-route-tooltip {
  .p-tooltip-text {
    width: 30rem;
  }
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.list-enter-to,
.list-leave-from {
  opacity: 1;
  transform: scale(1);
}
.list-enter-active,
.list-leave-active,
.list-move {
  transition: all 0.3s ease;
}
.list-leave-active {
  position: absolute;
}

.footer-enter-from,
.footer-leave-to {
  opacity: 0.5;
}

.footer-enter-to,
.footer-leave-from {
  opacity: 1;
}

.footer-enter-active,
.footer-leave-active,
.footer-move {
  transition: all 0.3s ease;
}
</style>
